import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage, localizePath, stripLanguagePrefix } from '../contexts/LanguageContext';
import seo from '../seo/pages.json';

const OG_LOCALES = { fr: 'fr_FR', en: 'en_US' };

// Crée ou met à jour une balise du <head> repérée par son sélecteur.
const upsert = (selector, tagName, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement(tagName);
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  return element;
};

const absoluteUrl = (path) => `${seo.siteUrl}${path === '/' ? '/' : path}`;

// Titre, description, canonical, hreflang, aperçus de partage et données d'entreprise de chaque page.
// Le script de pré-rendu (scripts/prerender.js) fige ce résultat dans le HTML de chaque adresse.
const Seo = () => {
  const { pathname } = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    const basePath = stripLanguagePrefix(pathname);
    const page = seo.pages.find((p) => p.path === basePath) || seo.pages[0];
    const { title, description } = page[language];
    const url = absoluteUrl(localizePath(page.path, language));
    const image = `${seo.siteUrl}${seo.image}`;

    document.title = title;
    upsert('meta[name="description"]', 'meta', { name: 'description', content: description });
    upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: url });
    upsert('link[rel="alternate"][hreflang="fr"]', 'link', { rel: 'alternate', hreflang: 'fr', href: absoluteUrl(page.path) });
    upsert('link[rel="alternate"][hreflang="en"]', 'link', { rel: 'alternate', hreflang: 'en', href: absoluteUrl(localizePath(page.path, 'en')) });
    upsert('link[rel="alternate"][hreflang="x-default"]', 'link', { rel: 'alternate', hreflang: 'x-default', href: absoluteUrl(page.path) });

    const og = {
      'og:type': 'website',
      'og:site_name': seo.siteName,
      'og:title': title,
      'og:description': description,
      'og:url': url,
      'og:image': image,
      'og:image:width': '1200',
      'og:image:height': '630',
      'og:locale': OG_LOCALES[language],
      'og:locale:alternate': OG_LOCALES[language === 'fr' ? 'en' : 'fr'],
    };
    Object.entries(og).forEach(([property, content]) =>
      upsert(`meta[property="${property}"]`, 'meta', { property, content })
    );

    const twitter = {
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': image,
    };
    Object.entries(twitter).forEach(([name, content]) =>
      upsert(`meta[name="${name}"]`, 'meta', { name, content })
    );

    upsert('script#ld-organization', 'script', { id: 'ld-organization', type: 'application/ld+json' })
      .textContent = JSON.stringify(seo.organization);
  }, [pathname, language]);

  return null;
};

export default Seo;
